import React from "react";
import classes from "../memory.module.css";
import CustomModal from "../../../components/Modals/CustomModal";
import TypographyInline from "../../../components/Typography/TypographyInline";
import { BASE, BORDER, GREY, WARNING } from "../../../utils/constants/colors";
import { fontWeight } from "../../../utils/constants/UI";
import Button from "../../../components/Buttons/Button";
import HtmlEditor from "../../../components/Editor/HtmlEditor";

const MemoryPreviewModal = ({
  open,
  editorSeed,
  metadata,
  sourceLabel,
  onSummaryChange,
  onClose,
  onAddMoreSources,
  onSave,
  isSaving,
}) => {
  const hasMeta =
    metadata?.message ||
    metadata?.recommendation ||
    metadata?.confidence ||
    typeof metadata?.grounded === "boolean" ||
    metadata?.source;

  return (
    <CustomModal
      width="66vw"
      blurBackdrop={true}
      open={open}
      onClose={onClose}
      header={{
        heading: {
          label: "Review generated knowledge",
        },
      }}
      footer={{
        right: (
          <div className={classes.actionButtons}>
            <Button
              label="Cancel"
              variant="ghost"
              size="sm36"
              onClick={onClose}
            />
            <Button
              label="Add More Sources"
              variant="ghost"
              size="sm36"
              onClick={onAddMoreSources}
            />
            <Button
              label={isSaving ? "Saving..." : "Save to Knowledge Base"}
              variant="primary"
              size="sm36"
              disabled={isSaving || !metadata?.hasUsableSummary}
              onClick={onSave}
            />
          </div>
        ),
      }}
    >
      <div className={classes.previewModalBody}>
        {metadata?.summaryMode === "research_fallback" && (
          <div className={classes.warningBanner}>
            <TypographyInline
              label="This result used research fallback and may be less grounded than a PDF or direct company source."
              size="sm"
              fontWeight={fontWeight.SEMIBOLD}
              color={WARNING}
            />
          </div>
        )}

        <div className={classes.previewHeader}>
          <div>
            <TypographyInline
              label="Review, edit, and save the generated summary."
              size="md"
              fontWeight={fontWeight.SEMIBOLD}
              color={BASE.BLACK}
            />
            <TypographyInline
              label="You can add more sources later to expand or improve this version."
              size="sm"
              fontWeight={fontWeight.REGULAR}
              color={GREY.SECONDARY}
            />
          </div>

          <div className={classes.previewBadgeRow}>
            {sourceLabel && (
              <span className={classes.metaBadge}>{sourceLabel}</span>
            )}
            {metadata?.confidence && (
              <span className={classes.metaBadge}>
                Confidence: {metadata.confidence}
              </span>
            )}
            {typeof metadata?.grounded === "boolean" && (
              <span
                className={`${classes.metaBadge} ${
                  metadata.grounded
                    ? classes.metaBadgeSuccess
                    : classes.metaBadgeWarning
                }`}
              >
                {metadata.grounded ? "Grounded" : "Not grounded"}
              </span>
            )}
          </div>
        </div>

        {hasMeta && (
          <div className={classes.previewMetaPanel}>
            {metadata?.message && (
              <TypographyInline
                label={metadata.message}
                size="sm"
                fontWeight={fontWeight.SEMIBOLD}
                color={BASE.BLACK}
              />
            )}
            {metadata?.recommendation && (
              <TypographyInline
                label={metadata.recommendation}
                size="sm"
                fontWeight={fontWeight.REGULAR}
                color={BASE.BLACK}
              />
            )}
            {metadata?.source && (
              <TypographyInline
                label={`Source: ${metadata.source}`}
                size="sm"
                fontWeight={fontWeight.REGULAR}
                color={BASE.BLACK}
              />
            )}
          </div>
        )}

        {metadata?.hasUsableSummary ? (
          <div
            className={classes.editorFrame}
            style={{
              border: `1px solid ${BORDER.LIGHT}`,
            }}
          >
            <HtmlEditor
              id="summary-preview-editor"
              previosData={editorSeed}
              onInput={onSummaryChange}
              editorHeight="380px"
            />
          </div>
        ) : (
          <div className={classes.previewEmptyState}>
            <TypographyInline
              label="No grounded summary was generated from this source."
              size="lg"
              fontWeight={fontWeight.BOLD}
              color={BASE.BLACK}
            />
            <TypographyInline
              label={
                metadata?.recommendation ||
                "Upload a PDF or add manual company details to create a grounded version you can save."
              }
              size="md"
              fontWeight={fontWeight.REGULAR}
              color={GREY.SECONDARY}
            />
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default MemoryPreviewModal;
